/**
 * Shared display helpers for flyers (Fresh Meet).
 */

/** First name: nickname if set, then firstName if set, otherwise first word of properName or name. */
export function getFirstName(flyer) {
  if (!flyer) return '';
  if (flyer.nickName != null && String(flyer.nickName).trim() !== '') return flyer.nickName.trim();
  const explicitFirst = (flyer.firstName || '').toString().trim();
  if (explicitFirst) return explicitFirst;
  const full = (flyer.properName || flyer.name || '').toString().trim();
  const first = full.split(/\s+/)[0];
  return first || '';
}

export function getFreshlingDisplayName(flyer) {
  if (!flyer) return '';
  if (flyer.type !== 'freshling') return flyer.name || '';
  const first = getFirstName(flyer);
  let last = (flyer.lastName || '').toString().trim();
  if (!last) {
    const full = (flyer.properName || flyer.name || '').toString().trim();
    const parts = full.split(/\s+/).filter(Boolean);
    if (parts.length > 1) {
      last = parts.slice(1).join(' ');
    }
  }
  if (first && last) return `${first} ${last}`;
  if (flyer.properName) return flyer.properName;
  if (first || last) return first || last;
  return flyer.name || '';
}

export function getDisplayName(flyer) {
  if (!flyer) return '';
  if (flyer.type === 'freshling') return getFreshlingDisplayName(flyer);
  return (flyer.name || '');
}

/**
 * Label for volunteer shift assignee rows when Firestore has a missing or generic displayName
 * (e.g. server default "Volunteer" → "VO" initials). Uses flyers keyed by identityUid.
 * @param {{ displayName?: string, uid?: string } | null | undefined} assignee
 * @param {Map<string, object>} flyerByIdentityUid
 */
export function volunteerAssigneeDisplayName(assignee, flyerByIdentityUid) {
  const raw = (assignee?.displayName || '').toString().trim();
  const uid = (assignee?.uid || '').toString().trim();
  const flyer = uid && flyerByIdentityUid instanceof Map ? flyerByIdentityUid.get(uid) : null;
  const fromFlyer = flyer ? (getDisplayName(flyer) || (flyer.email || '').toString()).trim() : '';
  const generic = !raw || raw.toLowerCase() === 'volunteer';
  if (fromFlyer && generic) return fromFlyer.slice(0, 120);
  if (raw) return raw.slice(0, 120);
  return (fromFlyer || uid || '').slice(0, 120);
}

/** Last name: explicit lastName, else last word of display name / name (for mentors with a single combined field). */
export function getLastName(flyer) {
  if (!flyer) return '';
  const last = (flyer.lastName || '').toString().trim();
  if (last) return last;
  const full = getDisplayName(flyer) || flyer.name || '';
  const parts = full.trim().split(/\s+/);
  return parts.length > 1 ? parts[parts.length - 1] : '';
}

export function getAgeFromYear(yearBorn) {
  if (yearBorn == null || yearBorn === '') return null;
  const y = typeof yearBorn === 'number' ? yearBorn : parseInt(String(yearBorn).trim(), 10);
  if (!Number.isFinite(y)) return null;
  return new Date().getFullYear() - y;
}

/** Day name (full or abbreviated) -> single letter. W, R, F, S, N for Wed–Sun. */
const DAY_TO_LETTER = {
  monday: 'M', mon: 'M',
  tuesday: 'T', tue: 'T',
  wednesday: 'W', wed: 'W',
  thursday: 'R', thu: 'R',
  friday: 'F', fri: 'F',
  saturday: 'S', sat: 'S',
  sunday: 'N', sun: 'N',
};

/** Single-letter day code -> expanded label for emails. */
const LETTER_TO_EXPANDED_DAY = {
  M: 'Mon',
  T: 'Tue',
  W: 'Wed',
  R: 'Thu',
  F: 'Fri',
  S: 'Sat',
  N: 'Sun',
};

/**
 * Abbreviate a days string to letters: W, R, F, S, N (and M, T for Mon/Tue).
 * e.g. "Wednesday, Thursday, Friday" -> "W, R, F"
 */
export function abbreviateDays(daysStr) {
  if (daysStr == null || String(daysStr).trim() === '') return '';
  const raw = String(daysStr).trim();
  const parts = raw.split(/[\s,;/]+/).map(p => p.trim()).filter(Boolean);
  const letters = [];
  const seen = new Set();
  for (const part of parts) {
    const key = part.toLowerCase();
    const letter = DAY_TO_LETTER[key] || (key.length <= 3 ? key.charAt(0).toUpperCase() : null);
    if (letter && !seen.has(letter)) {
      seen.add(letter);
      letters.push(letter);
    }
  }
  return letters.join(', ');
}

/**
 * Expand a days string to email-friendly labels like "Wed, Thu, Fri, Sat, Sun".
 * Uses the same parsing rules as abbreviateDays, then maps letters to names.
 */
export function expandDays(daysStr) {
  const lettersStr = abbreviateDays(daysStr);
  if (!lettersStr) return '';
  const rawParts = lettersStr.split(/[\s,]+/).filter(Boolean);
  const names = [];
  const seen = new Set();
  for (const part of rawParts) {
    const letter = part.toUpperCase();
    const name = LETTER_TO_EXPANDED_DAY[letter];
    if (name && !seen.has(name)) {
      seen.add(name);
      names.push(name);
    }
  }
  return names.join(', ');
}
